import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';

import { injectCrmSlice } from '@/app/crmApi';

import './__mocks__';
import { copyMockVersionSettings, mirrorWorkflow } from './__mocks__';
import {
  automationMetaOf,
  copyVersionSidecar,
  readVersionSidecar,
} from './__mocks__/sidecar';
import {
  CreateDraftFromPublishedResultDto,
  CreateWorkflowDefinitionResultDto,
  PublishVersionResultDto,
  ValidateWorkflowDocumentResultDto,
  WorkflowDefinitionDetailsDto,
  WorkflowDefinitionDto,
  WorkflowDefinitionStateOptionDto,
} from './dto';
import {
  MappedBoardLayout,
  MappedWorkflowVersion,
  applyStructure,
  fromAutomations,
  fromCreateDefinitionResult,
  fromDefinitionDetails,
  fromDefinitionList,
  fromStatusOptions,
  fromTransitionRules,
  fromValidation,
  toAutomationDtos,
  toCreateDefinitionRequest,
} from './mappers';
import {
  ApproveEntityTransitionPayload,
  ApproverOptions,
  AutomationEmailTemplate,
  AutomationRule,
  ChangeWorkflowEntityStatusPayload,
  CreateEntityTransitionRequestPayload,
  CreateWorkflowDraftPayload,
  CreateWorkflowDraftResponse,
  CreateWorkflowEntityPayload,
  CreateWorkflowEntityResponse,
  CreateWorkflowPayload,
  CreateWorkflowResponse,
  EntityStepFormValuesArgs,
  EntityTransitionRequest,
  EntityTransitionRequestArgs,
  GlobalStatusCatalogItem,
  ProcessSlaSettings,
  PublishWorkflowResponse,
  SaveAutomationRulesPayload,
  SaveBoardLayoutPayload,
  SaveDraftStructurePayload,
  SaveProcessSlaSettingsPayload,
  StepFormDictionary,
  StepFormValues,
  TransitionInputField,
  TransitionRestriction,
  TransitionRules,
  TransitionRulesArgs,
  TransitionValidator,
  UpdateWorkflowEntityPayload,
  UpdateWorkflowPayload,
  Workflow,
  WorkflowDraftValidation,
  WorkflowEntityHistoryItem,
  WorkflowEntityItem,
  WorkflowStatusCatalogItem,
  WorkflowVersionArgs,
  WorkflowVersionListItem,
  WorkflowVersionState,
} from './types';
import {
  DEFINITIONS_API,
  baseDraft,
  baseVersion,
  call,
  forgetWorkflowVersions,
  loadBoard,
  loadVersion,
  loadVersionList,
  run,
  saveBoard,
  saveDraft,
  workflowPath,
} from './workflowDefinitionsClient';

/**
 * Cache id of the cross-workflow status catalog — it belongs to no single
 * workflow, so anything that adds or removes a status has to invalidate it on
 * top of the workflow's own catalog entry.
 */
const ALL_WORKFLOWS = 'ALL';

/** A request changes the card, its tile and its history. */
const transitionRequestTags = (workflowId: string, entityId: string) => [
  { type: 'WorkflowEntities' as const, id: workflowId },
  { type: 'WorkflowEntity' as const, id: entityId },
  { type: 'WorkflowEntityHistory' as const, id: entityId },
  { type: 'WorkflowEntityTransitionRequest' as const, id: entityId },
];

// The fixture's endpoints keep the old base url; the ones the Workflows API
// serves override it through `extraOptions: DEFINITIONS_API`.
export const workflowBuilderApi = injectCrmSlice({
  baseUrl: Config.VITE_WORKFLOWS_API_URL,
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: [
    'WorkflowList',
    'Workflow',
    'WorkflowVersion',
    'WorkflowStatusCatalog',
    'WorkflowEntities',
    'WorkflowEntity',
    'WorkflowEntityHistory',
    'WorkflowBoardLayout',
    'WorkflowTransitionRules',
    'WorkflowEntityStepFormValues',
    'WorkflowAutomationRules',
    'WorkflowSlaSettings',
    'WorkflowEntityTransitionRequest',
  ],
  endpoints: builder => ({
    /**
     * The list rows — one per published or draft version in `states`. Served
     * by the Workflows API, like everything up to `getWorkflowEntities`.
     */
    getWorkflowVersions: builder.query<
      WorkflowVersionListItem[],
      WorkflowVersionState[]
    >({
      queryFn: (states, _api, _extra, baseQuery) =>
        run(async () =>
          fromDefinitionList(
            await call<WorkflowDefinitionDto[]>(baseQuery, {
              url: '/workflow-definitions',
              method: 'GET',
            }),
            states,
          ),
        ),
      extraOptions: DEFINITIONS_API,
      providesTags: ['WorkflowList'],
    }),
    /** The workflow's own fields and its versions, without their structure. */
    getWorkflow: builder.query<Workflow, string>({
      queryFn: (id, _api, _extra, baseQuery) =>
        run(async () => {
          const [details, versions] = await Promise.all([
            call<WorkflowDefinitionDetailsDto>(baseQuery, {
              url: workflowPath(id),
              method: 'GET',
            }),
            loadVersionList(baseQuery, id),
          ]);
          const workflow = fromDefinitionDetails(details, versions);

          mirrorWorkflow({
            id: workflow.id,
            name: workflow.name,
            description: workflow.description,
            kind: workflow.kind,
            entityType: workflow.entityType,
            creationMode: workflow.creationMode,
          });

          return workflow;
        }),
      extraOptions: DEFINITIONS_API,
      providesTags: (_result, _error, id) => [{ type: 'Workflow', id }],
    }),
    /** One version with its statuses and transitions. */
    getWorkflowVersion: builder.query<
      MappedWorkflowVersion,
      WorkflowVersionArgs
    >({
      queryFn: ({ workflowId, versionId }, _api, _extra, baseQuery) =>
        run(() => loadVersion(baseQuery, workflowId, versionId)),
      extraOptions: DEFINITIONS_API,
      // Tagged by workflow, not by version: a draft save or a publish has no
      // version id at hand, and either may change several versions at once
      // (publish moves two of them), so they all refetch together.
      providesTags: (_result, _error, { workflowId }) => [
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /**
     * The «Наявний статус» catalog: distinct statuses of every workflow. The
     * service sends no key and no workflow names, see `fromStatusOptions`.
     */
    getAllWorkflowStatuses: builder.query<GlobalStatusCatalogItem[], void>({
      queryFn: (_arg, _api, _extra, baseQuery) =>
        run(async () =>
          fromStatusOptions(
            await call<WorkflowDefinitionStateOptionDto[]>(baseQuery, {
              url: '/workflow-definitions/status-options',
              method: 'GET',
            }),
          ),
        ),
      extraOptions: DEFINITIONS_API,
      providesTags: [{ type: 'WorkflowStatusCatalog', id: ALL_WORKFLOWS }],
    }),
    /**
     * Distinct statuses of every version of one workflow — the board reads
     * it, since its cards may sit in a status only an older version has. The
     * newest version supplies a key's name, colour and category.
     */
    getWorkflowStatuses: builder.query<WorkflowStatusCatalogItem[], string>({
      queryFn: (id, _api, _extra, baseQuery) =>
        run(async () => {
          const list = await loadVersionList(baseQuery, id);
          const versions = await Promise.all(
            [...list]
              .sort((left, right) => right.number - left.number)
              .map(version => loadVersion(baseQuery, id, version.id)),
          );
          const byKey = new Map<string, WorkflowStatusCatalogItem>();

          versions.forEach(version =>
            version.statuses.forEach(({ key, name, color, category }) => {
              if (!byKey.has(key))
                byKey.set(key, { key, name, color, category });
            }),
          );

          return [...byKey.values()];
        }),
      extraOptions: DEFINITIONS_API,
      providesTags: (_result, _error, id) => [
        { type: 'WorkflowStatusCatalog', id },
        { type: 'WorkflowVersion', id },
      ],
    }),
    /** Creates the workflow together with its draft v1 from the template. */
    createWorkflow: builder.mutation<
      CreateWorkflowResponse,
      CreateWorkflowPayload
    >({
      queryFn: (payload, _api, _extra, baseQuery) =>
        run(async () =>
          fromCreateDefinitionResult(
            await call<CreateWorkflowDefinitionResultDto>(baseQuery, {
              url: '/workflow-definitions',
              method: 'POST',
              data: toCreateDefinitionRequest(payload),
            }),
          ),
        ),
      extraOptions: DEFINITIONS_API,
      // A new `Foreground` workflow is seeded with the template statuses, so
      // the cross-workflow catalog gains them right away.
      invalidatesTags: [
        'WorkflowList',
        { type: 'WorkflowStatusCatalog', id: ALL_WORKFLOWS },
      ],
    }),
    /** `kind` and `creationMode` are immutable. */
    updateWorkflow: builder.mutation<void, UpdateWorkflowPayload>({
      queryFn: (payload, _api, _extra, baseQuery) =>
        run(async () => {
          await call(baseQuery, {
            url: workflowPath(payload.id),
            method: 'PUT',
            data: payload,
          });
        }),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, { id }) => [
        'WorkflowList',
        { type: 'Workflow', id },
      ],
    }),
    /**
     * Copies the published version into a new draft. Publishing leaves the
     * workflow without one, so this is how further changes start.
     */
    createWorkflowDraft: builder.mutation<
      CreateWorkflowDraftResponse,
      CreateWorkflowDraftPayload
    >({
      queryFn: ({ workflowId, fromVersionId }, _api, _extra, baseQuery) =>
        run(async () => {
          const result = await call<CreateDraftFromPublishedResultDto>(
            baseQuery,
            {
              url: `${workflowPath(workflowId)}/draft`,
              method: 'POST',
              data: { creationMode: 'Manual' },
            },
          );

          // What the document cannot hold yet travels into the draft too.
          copyVersionSidecar(fromVersionId, result.versionId);
          copyMockVersionSettings(fromVersionId, result.versionId);

          return { versionId: result.versionId, number: result.number };
        }),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, { workflowId }) => [
        'WorkflowList',
        { type: 'Workflow', id: workflowId },
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /**
     * Replaces the draft structure — send the full lists — together with the
     * transition rules edited in the same session. Patches the draft's
     * document, so the rules and fields the builder does not edit stay.
     */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateDraftStructure: builder.mutation<void, SaveDraftStructurePayload>({
      queryFn: (
        { workflowId, statuses, transitions, transitionRules },
        _api,
        _extra,
        baseQuery,
      ) =>
        run(async () => {
          const base = await baseDraft(baseQuery, workflowId);
          const { document, sidecar } = applyStructure(
            base.document,
            statuses,
            transitions,
            readVersionSidecar(base.id),
            transitionRules,
          );

          await saveDraft(baseQuery, workflowId, base, document, sidecar);
        }),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, { workflowId }) => [
        'WorkflowList',
        { type: 'Workflow', id: workflowId },
        { type: 'WorkflowVersion', id: workflowId },
        { type: 'WorkflowStatusCatalog', id: workflowId },
        { type: 'WorkflowStatusCatalog', id: ALL_WORKFLOWS },
        'WorkflowTransitionRules',
      ],
    }),
    /**
     * Checks the draft as saved, without changing it: what blocks a save and
     * what blocks publishing.
     */
    // The name has to contain `create`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    createWorkflowDraftValidation: builder.mutation<
      WorkflowDraftValidation,
      string
    >({
      queryFn: (workflowId, _api, _extra, baseQuery) =>
        // No document: the server checks its stored draft, not a copy cached
        // here that another editor may have replaced since.
        run(async () =>
          fromValidation(
            await call<ValidateWorkflowDocumentResultDto>(baseQuery, {
              url: `${workflowPath(workflowId)}/draft/validation`,
              method: 'POST',
              data: { document: null },
            }),
          ),
        ),
      extraOptions: DEFINITIONS_API,
    }),
    /**
     * Publishes the draft: it becomes the version the board runs, keeping its
     * id, and the previous published version is retired. No new draft is
     * created — `createWorkflowDraft` starts the next one.
     */
    // The name has to contain `create`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    createWorkflowPublication: builder.mutation<
      PublishWorkflowResponse,
      string
    >({
      queryFn: (workflowId, _api, _extra, baseQuery) =>
        run(async () => {
          const base = await baseDraft(baseQuery, workflowId);
          // No cards move between states yet (they are mocked), so there is
          // nothing to map; the server says so if it needs a map.
          const result = await call<PublishVersionResultDto>(baseQuery, {
            url: `${workflowPath(workflowId)}/publish`,
            method: 'POST',
            data: {
              expectedRevision: base.documentRevision,
              migrationMap: null,
            },
          });

          forgetWorkflowVersions(workflowId);
          return { versionId: result.versionId, number: result.number };
        }),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, id) => [
        'WorkflowList',
        { type: 'Workflow', id },
        { type: 'WorkflowVersion', id },
        { type: 'WorkflowStatusCatalog', id },
        { type: 'WorkflowStatusCatalog', id: ALL_WORKFLOWS },
        { type: 'WorkflowBoardLayout', id },
      ],
    }),
    // TODO: mocked — no Workflows API endpoint yet for the cards, their
    // requests and step forms, the lookups and the process SLA;
    // `__mocks__/index.ts` answers them.
    /** The board cards — entities the workflow currently governs. */
    getWorkflowEntities: builder.query<WorkflowEntityItem[], string>({
      query: id => ({
        url: `/workflows/${id}/entities`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'WorkflowEntities', id }],
    }),
    /** One card with everything the details dialog shows. */
    getWorkflowEntity: builder.query<
      WorkflowEntityItem,
      { workflowId: string; entityId: string }
    >({
      query: ({ workflowId, entityId }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { entityId }) => [
        { type: 'WorkflowEntity', id: entityId },
      ],
    }),
    /** Everything that happened to one card, oldest first. */
    getWorkflowEntityHistory: builder.query<
      WorkflowEntityHistoryItem[],
      { workflowId: string; entityId: string }
    >({
      query: ({ workflowId, entityId }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/history`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { entityId }) => [
        { type: 'WorkflowEntityHistory', id: entityId },
      ],
    }),
    /** The card's open transition request; `null` when it waits for none. */
    getWorkflowEntityTransitionRequest: builder.query<
      EntityTransitionRequest | null,
      EntityTransitionRequestArgs
    >({
      query: ({ workflowId, entityId }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/transition-request`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { entityId }) => [
        { type: 'WorkflowEntityTransitionRequest', id: entityId },
      ],
    }),
    /**
     * Asks for approval of the move into `toStatusKey`. The card stays where
     * it is until the request is approved.
     */
    createWorkflowEntityTransitionRequest: builder.mutation<
      EntityTransitionRequest,
      CreateEntityTransitionRequestPayload
    >({
      query: ({ workflowId, entityId, toStatusKey }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/transition-request`,
        method: 'POST',
        data: { toStatusKey },
      }),
      invalidatesTags: (_result, _error, { workflowId, entityId }) =>
        transitionRequestTags(workflowId, entityId),
    }),
    /** Withdraws the open request; the approvals given so far are dropped. */
    cancelWorkflowEntityTransitionRequest: builder.mutation<
      void,
      EntityTransitionRequestArgs
    >({
      query: ({ workflowId, entityId }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/transition-request`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { workflowId, entityId }) =>
        transitionRequestTags(workflowId, entityId),
    }),
    /**
     * Approves the open request for one of its approvers. `null` back means
     * the last approval moved the card; a request still there may be waiting
     * for the move (`awaitingMove`).
     */
    approveWorkflowEntityTransition: builder.mutation<
      EntityTransitionRequest | null,
      ApproveEntityTransitionPayload
    >({
      query: ({ workflowId, entityId, approverId }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/transition-request/approve`,
        method: 'POST',
        data: { approverId },
      }),
      invalidatesTags: (_result, _error, { workflowId, entityId }) =>
        transitionRequestTags(workflowId, entityId),
    }),
    /** Adds a card to the board. */
    createWorkflowEntity: builder.mutation<
      CreateWorkflowEntityResponse,
      CreateWorkflowEntityPayload
    >({
      query: ({ workflowId, ...data }) => ({
        url: `/workflows/${workflowId}/entities`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: 'WorkflowEntities', id: workflowId },
      ],
    }),
    /** Moves one entity to another status — what a board drop performs. */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateWorkflowEntityStatus: builder.mutation<
      void,
      ChangeWorkflowEntityStatusPayload
    >({
      query: ({ workflowId, entityId, ...data }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/status`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { workflowId, entityId }) => [
        { type: 'WorkflowEntities', id: workflowId },
        { type: 'WorkflowEntity', id: entityId },
        { type: 'WorkflowEntityHistory', id: entityId },
        { type: 'WorkflowEntityStepFormValues', id: entityId },
        { type: 'WorkflowEntityTransitionRequest', id: entityId },
      ],
    }),
    /** Saves the card's own fields — what the details dialog submits. */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateWorkflowEntity: builder.mutation<void, UpdateWorkflowEntityPayload>({
      query: ({ workflowId, entityId, ...data }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { workflowId, entityId }) => [
        { type: 'WorkflowEntities', id: workflowId },
        { type: 'WorkflowEntity', id: entityId },
        { type: 'WorkflowEntityHistory', id: entityId },
        { type: 'WorkflowEntityStepFormValues', id: entityId },
        { type: 'WorkflowEntityTransitionRequest', id: entityId },
      ],
    }),
    /**
     * The board layout: how statuses are grouped into columns. One per
     * workflow, shared by every version's board. Absent until the user saves
     * one — `columns: null` then means «show the default», one column per
     * status. Served by the Workflows API.
     */
    getBoardLayout: builder.query<MappedBoardLayout, string>({
      queryFn: (workflowId, _api, _extra, baseQuery) =>
        run(() => loadBoard(baseQuery, workflowId)),
      extraOptions: DEFINITIONS_API,
      providesTags: (_result, _error, workflowId) => [
        { type: 'WorkflowBoardLayout', id: workflowId },
      ],
    }),
    /** Replaces the layout wholesale; `columns: null` drops it back to default. */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateBoardLayout: builder.mutation<void, SaveBoardLayoutPayload>({
      queryFn: ({ workflowId, columns }, _api, _extra, baseQuery) =>
        run(() => saveBoard(baseQuery, workflowId, columns)),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: 'WorkflowBoardLayout', id: workflowId },
      ],
    }),
    /** Everyone a transition rule may name: roles, departments and users. */
    getApproverOptions: builder.query<ApproverOptions, void>({
      query: () => ({
        url: '/workflows/approvers',
        method: 'GET',
      }),
    }),
    /** The fields a transition may ask to fill in. */
    getTransitionInputFieldOptions: builder.query<TransitionInputField[], void>(
      {
        query: () => ({
          url: '/workflows/input-fields',
          method: 'GET',
        }),
      },
    ),
    /** The dictionaries a `PredefinedList` step form field may pick from. */
    getStepFormDictionaries: builder.query<StepFormDictionary[], void>({
      query: () => ({
        url: '/workflows/dictionaries',
        method: 'GET',
      }),
    }),
    /**
     * What the entity last filled into the status's step form — prefills the
     * dialog when it enters the status again. Empty when it never has.
     */
    getEntityStepFormValues: builder.query<
      StepFormValues,
      EntityStepFormValuesArgs
    >({
      query: ({ workflowId, entityId, statusKey }) => ({
        url: `/workflows/${workflowId}/entities/${entityId}/step-forms/${statusKey}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { entityId }) => [
        { type: 'WorkflowEntityStepFormValues', id: entityId },
        { type: 'WorkflowEntityTransitionRequest', id: entityId },
      ],
    }),
    /** The conditions a transition may be restricted by. */
    getTransitionRestrictionOptions: builder.query<
      TransitionRestriction[],
      void
    >({
      query: () => ({
        url: '/workflows/restrictions',
        method: 'GET',
      }),
    }),
    /** The checks a transition may run. */
    getTransitionValidatorOptions: builder.query<TransitionValidator[], void>({
      query: () => ({
        url: '/workflows/validators',
        method: 'GET',
      }),
    }),
    /**
     * The rules of one transition; a rule never configured comes back `null`.
     * Read from the version's document, merged with the side-car.
     */
    getTransitionRules: builder.query<TransitionRules, TransitionRulesArgs>({
      queryFn: (
        { workflowId, versionId, transitionKey },
        _api,
        _extra,
        baseQuery,
      ) =>
        run(async () => {
          const version = await loadVersion(baseQuery, workflowId, versionId);

          return fromTransitionRules(
            version.document,
            transitionKey,
            readVersionSidecar(versionId),
          );
        }),
      extraOptions: DEFINITIONS_API,
      providesTags: (
        _result,
        _error,
        { workflowId, versionId, transitionKey },
      ) => [
        {
          type: 'WorkflowTransitionRules',
          id: `${versionId}:${transitionKey}`,
        },
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /**
     * The rules of every transition of a version, keyed by the transition's
     * `key`; the board and the card read restrictions from it up front.
     */
    getVersionTransitionRules: builder.query<
      Record<string, TransitionRules>,
      WorkflowVersionArgs
    >({
      queryFn: ({ workflowId, versionId }, _api, _extra, baseQuery) =>
        run(async () => {
          const version = await loadVersion(baseQuery, workflowId, versionId);
          const sidecar = readVersionSidecar(versionId);

          return Object.fromEntries(
            version.document.transitions.map(transition => [
              transition.key,
              fromTransitionRules(version.document, transition.key, sidecar),
            ]),
          );
        }),
      extraOptions: DEFINITIONS_API,
      providesTags: (_result, _error, { workflowId, versionId }) => [
        { type: 'WorkflowTransitionRules', id: versionId },
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /** The email templates an automation action may send. */
    getAutomationEmailTemplates: builder.query<AutomationEmailTemplate[], void>(
      {
        query: () => ({
          url: '/workflows/email-templates',
          method: 'GET',
        }),
      },
    ),
    /** The version's global automation rules, in run order. */
    getAutomationRules: builder.query<AutomationRule[], WorkflowVersionArgs>({
      queryFn: ({ workflowId, versionId }, _api, _extra, baseQuery) =>
        run(async () => {
          const version = await loadVersion(baseQuery, workflowId, versionId);

          return fromAutomations(
            version.document,
            readVersionSidecar(versionId).automations,
            version.createdAt,
          );
        }),
      extraOptions: DEFINITIONS_API,
      providesTags: (_result, _error, { workflowId, versionId }) => [
        { type: 'WorkflowAutomationRules', id: versionId },
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /**
     * Replaces the version's rules wholesale, in run order. Saved right away
     * into the draft's document; only the draft's rules are editable.
     */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateAutomationRules: builder.mutation<void, SaveAutomationRulesPayload>({
      queryFn: ({ workflowId, versionId, rules }, _api, _extra, baseQuery) =>
        run(async () => {
          const base = await baseVersion(baseQuery, workflowId, versionId);

          await saveDraft(
            baseQuery,
            workflowId,
            base,
            { ...base.document, automations: toAutomationDtos(rules) },
            { automations: automationMetaOf(rules) },
          );
        }),
      extraOptions: DEFINITIONS_API,
      invalidatesTags: (_result, _error, { workflowId, versionId }) => [
        { type: 'WorkflowAutomationRules', id: versionId },
        { type: 'WorkflowVersion', id: workflowId },
      ],
    }),
    /** The version's process SLA, from the case's creation to its end. */
    getProcessSlaSettings: builder.query<
      ProcessSlaSettings,
      WorkflowVersionArgs
    >({
      query: ({ workflowId, versionId }) => ({
        url: `/workflows/${workflowId}/versions/${versionId}/sla-settings`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { versionId }) => [
        { type: 'WorkflowSlaSettings', id: versionId },
      ],
    }),
    /** Saved right away, outside the draft structure; only in a draft. */
    // The name has to contain `update`: `axiosBaseQuery` keys the idempotence
    // header off the endpoint name.
    updateProcessSlaSettings: builder.mutation<
      void,
      SaveProcessSlaSettingsPayload
    >({
      query: ({ workflowId, versionId, ...data }) => ({
        url: `/workflows/${workflowId}/versions/${versionId}/sla-settings`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { versionId }) => [
        { type: 'WorkflowSlaSettings', id: versionId },
      ],
    }),
  }),
});

export const {
  useApproveWorkflowEntityTransitionMutation,
  useCancelWorkflowEntityTransitionRequestMutation,
  useCreateWorkflowEntityTransitionRequestMutation,
  useGetApproverOptionsQuery,
  useGetAutomationEmailTemplatesQuery,
  useGetAutomationRulesQuery,
  useGetEntityStepFormValuesQuery,
  useGetProcessSlaSettingsQuery,
  useGetStepFormDictionariesQuery,
  useGetTransitionInputFieldOptionsQuery,
  useGetTransitionRestrictionOptionsQuery,
  useGetTransitionRulesQuery,
  useGetVersionTransitionRulesQuery,
  useLazyGetTransitionRulesQuery,
  useGetTransitionValidatorOptionsQuery,
  useGetWorkflowVersionsQuery,
  useGetWorkflowQuery,
  useGetWorkflowVersionQuery,
  useGetAllWorkflowStatusesQuery,
  useGetWorkflowStatusesQuery,
  useGetWorkflowEntitiesQuery,
  useGetWorkflowEntityQuery,
  useGetWorkflowEntityTransitionRequestQuery,
  useGetWorkflowEntityHistoryQuery,
  useGetBoardLayoutQuery,
  useCreateWorkflowMutation,
  useCreateWorkflowEntityMutation,
  useCreateWorkflowDraftMutation,
  useCreateWorkflowDraftValidationMutation,
  useCreateWorkflowPublicationMutation,
  useUpdateWorkflowMutation,
  useUpdateAutomationRulesMutation,
  useUpdateBoardLayoutMutation,
  useUpdateDraftStructureMutation,
  useUpdateProcessSlaSettingsMutation,
  useUpdateWorkflowEntityMutation,
  useUpdateWorkflowEntityStatusMutation,
} = workflowBuilderApi;
